/**
 * Catching a render that threw.
 *
 * There was nothing doing this, so any exception anywhere in the tree unmounted
 * the whole application and left a white window with no way back but killing
 * the app. That is the worst possible failure: it loses the conversation you
 * were in, it tells the person nothing, and it looks like the app is broken
 * rather than that one screen is.
 *
 * Two are mounted. One around the shell, which catches anything the others
 * miss; one around each screen, so a profile that throws is a profile that
 * throws and chat carries on behind it.
 *
 * A class, because this is the one thing hooks still cannot do.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Named in the message, so "Chat could not be drawn" beats "Error". */
  what?: string;
  /** Whole-window failures offer a reload; a screen offers to go Home. */
  fatal?: boolean;
};

type State = { error: Error | null };

export class Boundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    /* Kept in the console rather than sent anywhere. There is no error
       reporting service wired up, and inventing one silently is worse than
       leaving the stack where a developer can already find it. */
    console.error('[boundary]', this.props.what ?? 'app', error, info.componentStack);
  }

  /**
   * Clearing the error re-renders the children.
   *
   * Worth having on a screen: the usual cause is data that arrived in a shape
   * nothing expected, and moving away and back is often all it takes.
   */
  private retry = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const what = this.props.what ?? 'This screen';

    return (
      <div className="grid min-h-0 flex-1 place-items-center p-6">
        <div className="pugo-panel w-full max-w-[460px] rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 p-7 text-center shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_28%,var(--line-soft)),0_24px_60px_-24px_rgba(0,0,0,.5)]">
          <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-[calc(16px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--bad)_14%,transparent)] text-bad">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 8v5M12 16.5v.5" /><path d="M10.3 3.9 2.6 17.4A2 2 0 0 0 4.3 20.4h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
          </span>
          <h2 className="m-0 text-[19px] font-bold text-ink">{what} could not be drawn</h2>
          <p className="m-0 mt-1.5 text-[14px] leading-relaxed text-mute">
            Something went wrong rendering it. The rest of the app is still
            running, and nothing you had open has been lost.
          </p>

          {/* The message, not the stack. Enough for somebody to tell you what
              they saw; the stack is in the console for whoever needs it. */}
          <p className="m-0 mt-4 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5 text-left font-mono text-[12px] break-words text-dim shadow-[inset_0_0_0_1px_var(--line-soft)]">
            {error.message || String(error)}
          </p>

          <div className="mt-5 flex justify-center gap-2">
            <button
              type="button"
              onClick={this.retry}
              className="h-10 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-5 text-[14px] font-bold text-accent-fg transition-[background-color] hover:bg-accent-hi"
            >
              Try again
            </button>

            {this.props.fatal ? (
              <button
                type="button"
                onClick={() => location.reload()}
                className="h-10 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-5 text-[14px] font-semibold text-dim shadow-[inset_0_0_0_1px_var(--line)] transition-colors hover:text-ink"
              >
                Reload Pugo
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { location.hash = '#/home'; this.retry(); }}
                className="h-10 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-5 text-[14px] font-semibold text-dim shadow-[inset_0_0_0_1px_var(--line)] transition-colors hover:text-ink"
              >
                Go Home
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
